import React from 'react';

const Privacy = () => {
  return (
    <div className="legal-page">
      <div className="legal-header">
        <h2>Privacy Policy</h2>
        <p>Learn how we collect, use, and protect your personal information</p>
      </div>
      
      <div className="legal-content">
        <p className="last-updated">Last updated: {new Date().toLocaleDateString()}</p>
        
        <section>
          <h2>1. Introduction</h2>
          <p>
            Practice Talking ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains 
            how we collect, use, disclose, and safeguard your personal information when you use our platform and services.
          </p>
        </section>

        <section>
          <h2>2. Information We Collect</h2>
          
          <h3>2.1 Personal Information</h3>
          <p>We collect the following personal information when you use our services:</p>
          <ul>
            <li><strong>Account Information:</strong> Name, email address, phone number, profile picture</li>
            <li><strong>Profile Data:</strong> Professional background, skills, experience, bio, availability</li>
            <li><strong>Payment Information:</strong> Billing details, payment methods (processed securely through Stripe)</li>
            <li><strong>Communication Data:</strong> Messages, session notes, feedback, and reviews</li>
            <li><strong>Session Data:</strong> Booking details, session recordings (with consent), duration, and outcomes</li>
          </ul>

          <h3>2.2 Technical Information</h3>
          <ul>
            <li><strong>Device Data:</strong> IP address, browser type, operating system, device identifiers</li>
            <li><strong>Usage Analytics:</strong> Pages visited, features used, time spent on platform, click patterns</li>
            <li><strong>Performance Data:</strong> Connection quality, video/audio metrics during sessions</li>
            <li><strong>Cookies and Tracking:</strong> Authentication tokens, preferences, session management</li>
          </ul>
        </section>

        <section>
          <h2>3. How We Use Your Information</h2>
          <p>We use your personal information to:</p>
          
          <h3>3.1 Provide Our Services</h3>
          <ul>
            <li>Create and manage your account</li>
            <li>Facilitate connections between practitioners and providers</li>
            <li>Process payments and bookings</li>
            <li>Enable video/audio communications</li>
            <li>Provide customer support</li>
          </ul>

          <h3>3.2 Improve Our Platform</h3>
          <ul>
            <li>Analyze usage patterns to enhance user experience</li>
            <li>Develop new features and services</li>
            <li>Optimize platform performance and security</li>
            <li>Conduct research and analytics (anonymized)</li>
          </ul>

          <h3>3.3 Communications</h3>
          <ul>
            <li>Send transactional emails (booking confirmations, reminders)</li>
            <li>Provide customer support responses</li>
            <li>Share platform updates and new features (with consent)</li>
            <li>Send security alerts and account notifications</li>
          </ul>
        </section>

        <section>
          <h2>4. Information Sharing and Disclosure</h2>
          <p>We do not sell your personal information. We may share your information in these limited circumstances:</p>
          
          <h3>4.1 With Other Users</h3>
          <ul>
            <li>Profile information visible to potential session partners</li>
            <li>Session-related communication between practitioners and providers</li>
            <li>Reviews and ratings (anonymized when appropriate)</li>
          </ul>

          <h3>4.2 Service Providers</h3>
          <ul>
            <li><strong>Payment Processing:</strong> Stripe for secure payment transactions</li>
            <li><strong>Cloud Storage:</strong> Firebase for data storage and real-time features</li>
            <li><strong>Video Services:</strong> Agora for video/audio communications</li>
            <li><strong>Analytics:</strong> Usage analytics for platform improvement</li>
          </ul>

          <h3>4.3 Legal Requirements</h3>
          <ul>
            <li>Comply with applicable laws and regulations</li>
            <li>Respond to legal requests and court orders</li>
            <li>Protect our rights, property, and safety</li>
            <li>Prevent fraud and abuse</li>
          </ul>
        </section>

        <section>
          <h2>5. Data Security</h2>
          <p>We implement comprehensive security measures to protect your information:</p>
          <ul>
            <li><strong>Encryption:</strong> Data encrypted in transit and at rest</li>
            <li><strong>Access Controls:</strong> Limited access to authorized personnel only</li>
            <li><strong>Secure Infrastructure:</strong> Industry-standard security practices</li>
            <li><strong>Regular Audits:</strong> Continuous monitoring and security assessments</li>
            <li><strong>Incident Response:</strong> Rapid response to potential security breaches</li>
          </ul>
        </section>

        <section>
          <h2>6. Data Retention</h2>
          <p>We retain your information for different periods based on the type of data:</p>
          <ul>
            <li><strong>Account Data:</strong> Until account deletion or 2 years of inactivity</li>
            <li><strong>Session Records:</strong> 1 year after session completion (unless longer retention required)</li>
            <li><strong>Payment Data:</strong> As required by financial regulations (typically 7 years)</li>
            <li><strong>Support Communications:</strong> 3 years for quality assurance</li>
            <li><strong>Analytics Data:</strong> Aggregated and anonymized data may be retained indefinitely</li>
          </ul>
        </section>

        <section>
          <h2>7. Your Privacy Rights</h2>
          <p>You have the following rights regarding your personal information:</p>
          
          <h3>7.1 Access and Portability</h3>
          <ul>
            <li>Request a copy of your personal data</li>
            <li>Download your data in a structured format</li>
            <li>View and update your profile information</li>
          </ul>

          <h3>7.2 Correction and Updates</h3>
          <ul>
            <li>Correct inaccurate or incomplete information</li>
            <li>Update your profile and preferences</li>
            <li>Modify communication settings</li>
          </ul>

          <h3>7.3 Deletion</h3>
          <ul>
            <li>Request deletion of your account and associated data</li>
            <li>Remove specific pieces of information (where legally permissible)</li>
            <li>Withdraw consent for optional data processing</li>
          </ul>

          <h3>7.4 Restriction and Objection</h3>
          <ul>
            <li>Limit how we process your data</li>
            <li>Object to certain types of processing</li>
            <li>Opt out of marketing communications</li>
          </ul>
        </section>

        <section>
          <h2>8. Cookies and Tracking Technologies</h2>
          <p>We use cookies and similar technologies to:</p>
          <ul>
            <li><strong>Essential Cookies:</strong> Authentication, security, and basic functionality</li>
            <li><strong>Analytics Cookies:</strong> Understand usage patterns and improve our service</li>
            <li><strong>Preference Cookies:</strong> Remember your settings and preferences</li>
          </ul>
          <p>You can control cookie settings through your browser, though disabling essential cookies may affect platform functionality.</p>
        </section>

        <section>
          <h2>9. Third-Party Services</h2>
          <p>Our platform integrates with third-party services that have their own privacy policies:</p>
          <ul>
            <li><strong>Firebase/Google Cloud:</strong> Data storage and authentication</li>
            <li><strong>Stripe:</strong> Payment processing and financial transactions</li>
            <li><strong>Agora:</strong> Video and audio communication services</li>
          </ul>
          <p>We recommend reviewing the privacy policies of these services to understand their data practices.</p>
        </section>

        <section>
          <h2>10. International Data Transfers</h2>
          <p>
            Your information may be processed and stored in countries other than your country of residence. 
            We ensure appropriate safeguards are in place to protect your data during international transfers, 
            including standard contractual clauses and adequacy decisions.
          </p>
        </section>

        <section>
          <h2>11. Children's Privacy</h2>
          <p>
            Our service is not intended for individuals under the age of 18. We do not knowingly collect 
            personal information from children. If we become aware that a child has provided personal information, 
            we will take steps to delete such information promptly.
          </p>
        </section>

        <section>
          <h2>12. Changes to This Privacy Policy</h2>
          <p>
            We may update this Privacy Policy periodically to reflect changes in our practices or applicable laws. 
            We will notify you of significant changes through email or platform notifications. Your continued use 
            of our services after such notifications constitutes acceptance of the updated policy.
          </p>
        </section>

        <section>
          <h2>13. California Privacy Rights (CCPA)</h2>
          <p>
            California residents have additional rights under the California Consumer Privacy Act (CCPA), including:
          </p>
          <ul>
            <li>Right to know what personal information is collected and how it's used</li>
            <li>Right to delete personal information</li>
            <li>Right to opt out of the sale of personal information (we do not sell personal information)</li>
            <li>Right to non-discrimination for exercising privacy rights</li>
          </ul>
        </section>

        <section>
          <h2>14. European Privacy Rights (GDPR)</h2>
          <p>
            If you are in the European Economic Area, you have additional rights under the General Data Protection 
            Regulation (GDPR), including all rights listed in Section 7, plus the right to lodge a complaint 
            with your local data protection authority.
          </p>
        </section>

        <section>
          <h2>15. Contact Information</h2>
          <p>
            If you have questions about this Privacy Policy or want to exercise your privacy rights, please contact us:
          </p>
          <ul>
            <li><strong>Email:</strong> privacy@practicetalking.com</li>
            <li><strong>Support:</strong> Through our Help & Support page</li>
            <li><strong>Mail:</strong> Practice Talking Privacy Team, [Address to be added]</li>
          </ul>
          <p>
            For urgent privacy concerns or data protection officer inquiries, please use the email address above 
            with "URGENT - Privacy" in the subject line.
          </p>
        </section>
      </div>
    </div>
  );
};

export default Privacy;